import { useEffect, useRef, type KeyboardEvent } from "react"
import { motion, useReducedMotion } from "motion/react"
import { cx } from "@/lib/utils/cx"

// The camera's "More": what a person needs now and then (the voice guide, an
// example photo, the phone's own camera), kept off the picture until asked
// for. A small sheet above the controls (beside them when sideways); focus
// goes in, stays in (Tab wraps), and Escape or Done closes it (the camera
// screen handles Escape).

export interface MoreItem {
  key: string
  label: string
  onSelect: () => void
  // A switch (aria-checked) instead of a plain action.
  checked?: boolean
}

export function MoreSheet({
  items,
  side,
  onClose,
}: {
  items: readonly MoreItem[]
  // Where it opens from: the bottom (upright), or the controls rail.
  side: "bottom" | "right" | "left"
  onClose: () => void
}) {
  const reduce = useReducedMotion()
  const panel = useRef<HTMLDivElement>(null)
  useEffect(() => {
    panel.current?.querySelector<HTMLElement>("button")?.focus({ preventScroll: true })
  }, [])

  // Tab wraps inside the sheet.
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "Tab" || !panel.current) return
    const all = [...panel.current.querySelectorAll<HTMLElement>("button")]
    const first = all[0]
    const last = all[all.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last?.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first?.focus()
    }
  }

  const row =
    "flex h-12 w-full items-center justify-between gap-4 rounded-xl px-4 text-left text-[16px] font-medium text-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white"
  return (
    <>
      <div aria-hidden onClick={onClose} className="absolute inset-0 z-40 bg-black/30" />
      <motion.div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="More"
        onKeyDown={onKeyDown}
        data-testid="more-sheet"
        initial={reduce ? false : { opacity: 0, y: side === "bottom" ? 16 : 0, x: side === "right" ? 16 : side === "left" ? -16 : 0 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.18, ease: [0.2, 0, 0, 1] }}
        className={cx(
          "absolute z-50 w-[min(320px,calc(100%-24px))] rounded-3xl bg-zinc-950/95 p-2 shadow-2xl ring-1 ring-white/10 backdrop-blur-xl",
          side === "bottom" && "inset-x-0 bottom-[max(env(safe-area-inset-bottom),12px)] mx-auto",
          // Beside the shutter rail (92px plus the notch's safe area).
          side === "right" && "right-[calc(104px+max(env(safe-area-inset-right),0px))] top-1/2 -translate-y-1/2",
          side === "left" && "left-[calc(104px+max(env(safe-area-inset-left),0px))] top-1/2 -translate-y-1/2",
        )}
      >
        <ul>
          {items.map((item) => (
            <li key={item.key}>
              {item.checked === undefined ? (
                <button type="button" className={row} onClick={item.onSelect}>
                  {item.label}
                </button>
              ) : (
                <button type="button" role="switch" aria-checked={item.checked} className={row} onClick={item.onSelect}>
                  {item.label}
                  <span
                    aria-hidden
                    className={cx(
                      "relative h-7 w-12 shrink-0 rounded-full transition-colors duration-150",
                      item.checked ? "bg-signal-on-dark" : "bg-white/25",
                    )}
                  >
                    <span
                      className={cx(
                        "absolute top-0.5 size-6 rounded-full bg-white shadow transition-transform duration-150",
                        item.checked ? "translate-x-[22px]" : "translate-x-0.5",
                      )}
                    />
                  </span>
                </button>
              )}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={onClose}
          className="mt-1 h-12 w-full rounded-xl bg-white/10 text-[16px] font-semibold text-white hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-white"
        >
          Done
        </button>
      </motion.div>
    </>
  )
}
